import { useState, useEffect } from 'react';
import useSWR from 'swr';

import { IRepository, IResourceVersion } from '@/types';
import { IResponseError } from '@/lib/apiBase';
import { fetchNotebookConfig, INotebookConfig, INotebookConfigResponse } from '@/lib/notebooks/notebooksApi';
import { FOCUS_THROTTLE_INTERVAL } from '@/config/appSettings';

interface useNotebookConfigParams {
  repository: IRepository;
  resource?: IResourceVersion;
  initialData?: INotebookConfigResponse | undefined;
}

const useNotebookConfig = ({ repository, resource, initialData }: useNotebookConfigParams) => {
  const [status, setStatus] = useState<'success' | 'error' | 'pending'>('pending');
  const [notebookConfig, setNotebookConfig] = useState<INotebookConfig | undefined>(initialData?.notebook_config);
  const [error, setError] = useState<IResponseError | undefined>();

  const branch = resource?.version || 'main';
  const path = resource?.path;
  const pathWithBranch = [branch, path].join('/');

  const fetchKey = ['notebook_config', repository.namespace, repository.name, pathWithBranch];

  const {
    data,
    isLoading,
    isValidating,
    // error: requestError,
  } = useSWR(
    fetchKey,
    () =>
      fetchNotebookConfig({
        namespace: repository.namespace,
        repositoryName: repository.name,
        resourcePath: pathWithBranch,
      }),
    {
      keepPreviousData: true,
      revalidateOnFocus: false,
      revalidateOnMount: !initialData,
      revalidateOnReconnect: false,
      focusThrottleInterval: FOCUS_THROTTLE_INTERVAL,
    },
  );

  useEffect(() => {
    if (data) {
      setNotebookConfig(data?.notebook_config);
      setStatus(data?.status);
      setError(data?.error as IResponseError);
    }
  }, [data]);

  return {
    status: status,
    isLoading: isLoading,
    isValidating: isValidating,
    notebookConfig: notebookConfig,
    resource: resource,
    error: error,
  };
};

export default useNotebookConfig;
